import { AxiosInstance } from 'axios';
import { useApiRequest } from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';
import { maxBy } from 'lodash';
import moment from 'moment';
import { useCallback, useState, useMemo } from 'react';
import { dateFormatForAPI } from 'utils/date';

export interface Discount {
  id: number;
  amount: number;
  title: string;
  venueIds: number[];
  spaceIds: number[];
  roomTypeIds: number[];
}

export interface Discounts {
  discounts: Discount[];
}

interface DiscountParams {
  spaceIds?: number[];
  roomTypeIds?: number[];
  venueIds?: number[];
}

export const getDiscounts = async (
  api: AxiosInstance,
  params: DiscountParams = {}
) => {
  const {
    data: { discounts },
  } = await api.get<Discounts>('/v1/discounts/search', {
    params: {
      ...params,
      date: moment().format(dateFormatForAPI),
    },
  });

  return discounts;
};

export const getDiscount = async (
  api: AxiosInstance,
  params: DiscountParams = {}
) => {
  const discounts = await getDiscounts(api, params);

  return maxBy(discounts, discount => discount.amount);
};

const usePropertyDiscount = ({
  venueIds = [],
  spaceIds = [],
  roomTypeIds = [],
}: DiscountParams) => {
  const [discounts, setDiscounts] = useState<Discount[]>(undefined);

  const jsonParams = useMemo(
    () => JSON.stringify({ venueIds, spaceIds, roomTypeIds }),
    [venueIds, spaceIds, roomTypeIds]
  );

  const apiRequest = useApiRequest();

  const fetchData = useCallback(async () => {
    setDiscounts(await getDiscounts(apiRequest, JSON.parse(jsonParams)));
  }, [apiRequest, jsonParams]);

  const { loading, error, refresh } = useAsync(fetchData);

  return { discounts, loading, error, refresh };
};

export default usePropertyDiscount;
